import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Users,
  DollarSign,
  Wallet,
  CheckCircle2,
  XCircle,
  PlayCircle,
  Loader2,
} from "lucide-react";

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("af_token")}`,
});

export default function AdminAffiliates() {
  const [affiliates, setAffiliates] = useState([]);
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, s, r] = await Promise.all([
        axios.get(`${API}/admin/affiliates`, { headers: authHeader() }),
        axios.get(`${API}/admin/affiliates/stats`, { headers: authHeader() }),
        axios.get(`${API}/admin/affiliates/payouts/runs`, { headers: authHeader() }),
      ]);
      setAffiliates(a.data?.affiliates || []);
      setStats(s.data);
      setRuns(r.data?.runs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const suspend = async (userId) => {
    if (!confirm("Suspend this affiliate? They will stop accruing commission immediately.")) return;
    try {
      await axios.post(`${API}/admin/affiliates/${userId}/suspend`, {}, { headers: authHeader() });
      toast.success("Affiliate suspended");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to suspend");
    }
  };

  const reinstate = async (userId) => {
    try {
      await axios.post(`${API}/admin/affiliates/${userId}/reinstate`, {}, { headers: authHeader() });
      toast.success("Affiliate reinstated");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reinstate");
    }
  };

  const triggerPayouts = async () => {
    if (!confirm("Force-run payouts NOW for every affiliate over $50 pending? This bypasses the 1st-of-month gate.")) return;
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/affiliates/payouts/run`, {}, { headers: authHeader() });
      toast.success(`Payouts complete: ${r.data?.paid || 0} paid, ${r.data?.failed || 0} failed`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout run failed");
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="admin-affiliates-loading">
        <div className="h-8 w-64 bg-slate-100 animate-pulse rounded" />
        <div className="h-32 bg-slate-100 animate-pulse rounded-xl" />
      </div>
    );
  }

  const byStatus = stats?.by_status || {};
  const totalAffiliates = affiliates.length;
  const totalPending = Object.values(byStatus).reduce((s, r) => s + (r.pending_cents || 0), 0);
  const totalLifetime = Object.values(byStatus).reduce((s, r) => s + (r.lifetime_cents || 0), 0);
  const totalPaid = Object.values(byStatus).reduce((s, r) => s + (r.paid_cents || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-affiliates-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-display font-extrabold text-2xl tracking-tight">Affiliate Program</h2>
          <p className="text-[var(--ink-2)] text-sm mt-1">
            Active loop: 30% recurring for 12 months. Payouts run on the 1st of each month.
          </p>
        </div>
        <button
          onClick={triggerPayouts}
          disabled={running}
          className="btn-money"
          data-testid="trigger-payouts-btn"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
          {running ? "Running…" : "Force-run payouts"}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={Users} label="Affiliates" value={totalAffiliates} accent="#0F172A" />
        <Tile icon={DollarSign} label="Pending" value={`$${(totalPending / 100).toFixed(2)}`} accent="#F59E0B" />
        <Tile icon={Wallet} label="Lifetime accrued" value={`$${(totalLifetime / 100).toFixed(2)}`} accent="#3B82F6" />
        <Tile icon={CheckCircle2} label="Lifetime paid out" value={`$${(totalPaid / 100).toFixed(2)}`} accent="#10B981" />
      </div>

      {/* Affiliate list */}
      <section data-testid="affiliates-list-section">
        <h3 className="font-display font-bold mb-3">All affiliates</h3>
        {affiliates.length === 0 ? (
          <div className="card p-6 text-center text-[var(--ink-2)]">No affiliates yet.</div>
        ) : (
          <div className="card overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-[var(--ink-3)]">
                <tr>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Method</th>
                  <th className="text-right px-4 py-3">Pending</th>
                  <th className="text-right px-4 py-3">Lifetime</th>
                  <th className="text-right px-4 py-3">Paid out</th>
                  <th className="text-left px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line)]">
                {affiliates.map((a) => (
                  <tr key={a.user_id} data-testid={`affiliate-row-${a.user_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{a.user_name || "—"}</div>
                      <div className="text-xs text-[var(--ink-3)]">{a.user_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={
                          a.status === "approved"
                            ? { background: "#ECFDF5", color: "#065F46" }
                            : { background: "#FEE2E2", color: "#991B1B" }
                        }
                      >
                        {a.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs capitalize">{(a.payout_method || "").replace("_", " ")}</td>
                    <td className="px-4 py-3 text-right font-mono">${(a.pending_balance_cents / 100).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono">${(a.total_lifetime_commission_cents / 100).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono">${(a.total_lifetime_payout_cents / 100).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      {a.status === "approved" ? (
                        <button
                          onClick={() => suspend(a.user_id)}
                          className="btn-outline btn-sm text-xs"
                          data-testid={`suspend-btn-${a.user_id}`}
                        >
                          <XCircle size={12} /> Suspend
                        </button>
                      ) : (
                        <button
                          onClick={() => reinstate(a.user_id)}
                          className="btn-money btn-sm text-xs"
                          data-testid={`reinstate-btn-${a.user_id}`}
                        >
                          <CheckCircle2 size={12} /> Reinstate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Payout runs */}
      {runs.length > 0 && (
        <section data-testid="payout-runs-section">
          <h3 className="font-display font-bold mb-3">Payout run history</h3>
          <div className="card overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-[var(--ink-3)]">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-right px-4 py-3">Paid</th>
                  <th className="text-right px-4 py-3">Failed</th>
                  <th className="text-right px-4 py-3">Skipped</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line)]">
                {runs.map((r) => (
                  <tr key={r.day}>
                    <td className="px-4 py-3 text-xs">{r.day}</td>
                    <td className="px-4 py-3 text-right font-mono">{r.summary?.paid ?? "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">{r.summary?.failed ?? "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">{r.summary?.skipped ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent }) {
  return (
    <div className="card p-4">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
        style={{ background: accent + "1a", color: accent }}
      >
        <Icon size={18} />
      </div>
      <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] mb-1">{label}</div>
      <div className="font-display font-extrabold text-2xl tracking-tight">{value}</div>
    </div>
  );
}
